import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLinkedin, FiMail, FiMapPin, FiPhone } = FiIcons;

gsap.registerPlugin(ScrollTrigger);

const ContactSection = () => {
  const sectionRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    const s1 = document.createElement('script');
    s1.src = "https://formnx.com/js/iframeResizer.js";
    s1.async = true;
    document.body.appendChild(s1);

    const s2 = document.createElement('script');
    s2.src = "https://formnx.com/js/widget.js";
    s2.async = true;
    document.body.appendChild(s2);

    return () => {
      document.body.removeChild(s1);
      document.body.removeChild(s2);
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const letters = nameRef.current.querySelectorAll('.letter');
      gsap.fromTo(
        letters,
        { y: 100, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 1, stagger: 0.05, ease: "power4.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 60%", end: "bottom 90%", toggleActions: "play none none reverse" },
        }
      );

      gsap.from(".anim-item", {
        y: 30, opacity: 0, duration: 0.8, stagger: 0.1, ease: "power3.out",
        scrollTrigger: { trigger: sectionRef.current, start: "top 70%" },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const socialLinks = [
    { name: 'LinkedIn', url: 'https://www.linkedin.com/in/fernandoismee', icon: FiLinkedin },
  ];

  const handleNameHover = (e) => {
    const letters = e.currentTarget.querySelectorAll('.letter');
    gsap.to(letters, {
      y: (i) => (i % 2 === 0 ? -15 : 15),
      color: "#ffffff",
      textShadow: "0 0 20px rgba(255,255,255,0.5)",
      duration: 0.4, ease: "power2.out", stagger: 0.03,
    });
  };

  const handleNameLeave = (e) => {
    const letters = e.currentTarget.querySelectorAll('.letter');
    gsap.to(letters, {
      y: 0, color: "#ffffff", textShadow: "none",
      duration: 0.4, ease: "power2.out", stagger: 0.03,
    });
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative min-h-screen w-full bg-gradient-to-b from-[#0a0a0a] to-[#020202] flex flex-col justify-between pt-24 px-6 md:px-12 overflow-hidden border-t border-white/5 pb-6"
    >
      <div className="w-full max-w-7xl mx-auto z-10 flex-grow">
        <div className="anim-item mb-16 md:mb-24">
          <h2 className="text-4xl md:text-6xl font-light text-white leading-tight">
            Have an idea? <br />
            <span className="text-gray-500">Let's build it together.</span>
          </h2>
        </div>

        <div className="flex flex-col md:flex-row gap-16 md:gap-24">
          <div className="anim-item w-full md:w-3/5 order-2 md:order-1">
            <div className="w-full rounded-xl overflow-hidden bg-white/5 p-1 border border-white/10">
              <iframe
                id="formnx-17em11-rp8qprzu"
                src="https://fill.formnx.com/f/contact-fernando-17em11?iframe=true"
                frameBorder="0"
                style={{ width: '1px', minWidth: '100%', minHeight: '500px' }}
                title="Contact Form"
              />
            </div>
          </div>

          <div className="anim-item w-full md:w-2/5 flex flex-col gap-12 order-1 md:order-2">
            <div className="flex flex-col gap-8">
              <h3 className="text-sm font-medium text-white uppercase tracking-widest border-b border-white/10 pb-4 mb-2">
                Contact Details
              </h3>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 uppercase tracking-widest">Email</span>
                <a href="mailto:fernando.comisnot@gmail.com" className="text-lg md:text-xl text-gray-300 hover:text-white transition-colors font-light">
                  fernando.comisnot@gmail.com
                </a>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 uppercase tracking-widest">Phone</span>
                <p className="text-lg md:text-xl text-gray-300 font-light">+65 9385 2293</p>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 uppercase tracking-widest">Location</span>
                <p className="text-lg md:text-xl text-gray-300 font-light">Singapore</p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-xs text-gray-500 uppercase tracking-widest">Socials</span>
              <div className="flex items-center gap-4">
                {socialLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 hover:border-white transition-all duration-300 group"
                    title={link.name}
                  >
                    <SafeIcon icon={link.icon} className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-screen relative left-1/2 -translate-x-1/2 mt-20 md:mt-32 pb-4">
        <InteractiveString />
        <div
          ref={nameRef}
          className="w-full overflow-hidden select-none cursor-default flex justify-center -mt-8 relative z-10 pointer-events-none"
          onMouseEnter={handleNameHover}
          onMouseLeave={handleNameLeave}
        >
          <h1 className="text-[12vw] md:text-[14vw] font-black text-white text-center leading-[0.85] tracking-tighter uppercase flex justify-center items-center w-full whitespace-nowrap pointer-events-auto">
            {"FERNANDO".split("").map((char, i) => (
              <span key={i} className="letter inline-block transform-gpu will-change-transform px-[0.2vw] md:px-[0.4vw]">
                {char === " " ? "\u00A0" : char}
              </span>
            ))}
          </h1>
        </div>
        <div className="w-full flex justify-center mt-6 md:mt-4">
          <span className="text-gray-600 font-mono text-xs md:text-sm tracking-[0.2em] uppercase">
            © 2026 Fernando
          </span>
        </div>
      </div>
    </section>
  );
};

const InteractiveString = () => {
  const pathRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const setPath = () => {
      const width = window.innerWidth;
      if (pathRef.current) {
        pathRef.current.setAttribute("d", `M 0 50 Q ${width / 2} 50 ${width} 50`);
      }
    };
    setPath();
    window.addEventListener('resize', setPath);
    return () => window.removeEventListener('resize', setPath);
  }, []);

  const handleMouseMove = (e) => {
    if (!pathRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const width = window.innerWidth;
    gsap.to(pathRef.current, {
      attr: { d: `M 0 50 Q ${e.clientX} ${y} ${width} 50` },
      duration: 0.1, ease: "power2.out",
    });
  };

  const handleMouseLeave = () => {
    if (!pathRef.current) return;
    const width = window.innerWidth;
    gsap.to(pathRef.current, {
      attr: { d: `M 0 50 Q ${width / 2} 50 ${width} 50` },
      duration: 1.5, ease: "elastic.out(1,0.2)",
    });
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-24 relative z-50 cursor-crosshair"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <svg className="w-full h-full absolute top-0 left-0 pointer-events-none overflow-visible">
        <path ref={pathRef} d="" stroke="rgba(255,255,255,0.2)" strokeWidth="1" fill="transparent" />
      </svg>
    </div>
  );
};

export default ContactSection;